import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { theme } from '@garnish/constants';

import { BodyText, Button, TitleText, useResponsive } from '@sg/garnish';

import { LoyaltyHomeChallengesRewardsContainer } from '../LoyaltyHomeChallengesRewardsContainer';

// ───────────────────────────────────────────────────────────────────────────

export const LoyaltyHomeUnlockableRewards = (props: LoyaltyHomeUnlockableRewardsProps) => {
  const {
    children,
    isPremium,
    title,
    subtitle,
    availablePoints,
    onOrderToEarn,
    onForgotToScan,
    onPointsHistory,
  } = props;

  // ─── Styles ────────────────────────────────────────────────────────────────

  const { match } = useResponsive();
  const textColor = isPremium ? styles.textWhite : styles.textSpinach;
  const backgroundColor = isPremium ? styles.backgroundBlack : styles.backgroundWhite;

  // ─── Messages ──────────────────────────────────────────────────────────────

  const { formatMessage } = useIntl();
  const orderToEarn = formatMessage(messages.orderToEarn);
  const forgotToScan = formatMessage(messages.forgotToScan);
  const pointsHistory = formatMessage(messages.pointsHistory);

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <LoyaltyHomeChallengesRewardsContainer style={[styles.container, backgroundColor]}>
      <View style={styles.header}>
        <View style={styles.headerContent}>
          <TitleText style={textColor} sizeMatch={['40', '40', '48']}>
            {title}
          </TitleText>

          <BodyText style={textColor} sizeMatch={['18']}>
            {subtitle}
          </BodyText>
        </View>

        <View style={match([styles.headerCtasSM, styles.headerCtasSM, styles.headerCtasMD])}>
          {availablePoints === 0 ? (
            <View style={styles.cta}>
              <Button
                size="large"
                palette={isPremium ? 'white' : 'kaleLightest'}
                accessibilityLabel={orderToEarn}
                onPress={onOrderToEarn}
              >
                {orderToEarn}
              </Button>
            </View>
          ) : null}

          {availablePoints > 0 ? (
            <View style={styles.cta}>
              <Button
                size="large"
                palette={isPremium ? 'white' : 'secondary'}
                accessibilityLabel={forgotToScan}
                onPress={onForgotToScan}
              >
                {forgotToScan}
              </Button>
            </View>
          ) : null}

          {availablePoints > 0 ? (
            <View style={styles.cta}>
              <Button
                size="large"
                palette={isPremium ? 'white' : 'secondary'}
                accessibilityLabel={pointsHistory}
                onPress={onPointsHistory}
              >
                {pointsHistory}
              </Button>
            </View>
          ) : null}
        </View>
      </View>

      {children}
    </LoyaltyHomeChallengesRewardsContainer>
  );
};

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  title: {
    defaultMessage: 'Unlock-able rewards',
    description: 'Loyalty Unlockable Rewards | Title',
  },
  orderToEarn: {
    defaultMessage: 'Order to earn',
    description: 'Loyalty Unlockable Rewards | Order to earn | CTA',
  },
  forgotToScan: {
    defaultMessage: 'Forgot to scan',
    description: 'Loyalty Unlockable Rewards | Forgot to scan | CTA',
  },
  pointsHistory: {
    defaultMessage: 'Points history',
    description: 'Loyalty Unlockable Rewards | Points history | CTA',
  },
});

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    paddingVertical: theme.spacing['20'],
    gap: theme.spacing['8'],
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  headerContent: {
    gap: theme.spacing['4'],
  },
  headerCtasSM: {
    flexDirection: 'row',
    gap: theme.spacing['2'],
  },
  headerCtasMD: {
    flexDirection: 'row',
    gap: theme.spacing['4'],
  },
  cta: {
    height: 48,
  },
  textSpinach: {
    color: theme.colors.SPINACH,
  },
  textWhite: {
    color: theme.colors.WHITE,
  },
  backgroundWhite: {
    backgroundColor: theme.colors.WHITE,
  },
  backgroundBlack: {
    backgroundColor: theme.colors.BLACK,
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type LoyaltyHomeUnlockableRewardsProps = {
  children?: React.ReactNode;
  title: string;
  subtitle: string;
  isPremium: boolean;
  availablePoints: number;
  onOrderToEarn: () => void;
  onForgotToScan: () => void;
  onPointsHistory: () => void;
};
